import type { MeetDetail, ResultStatus } from "@seagames/contracts";

/**
 * The meet screen is ONE page, the old OVR's way (user, 2026-10-22): no tab
 * row. What it shows follows the meet's status — the start list before the
 * meet (no status, Start list), the result list from Live on (`meetMode`) —
 * with the meet form, always open, above it. The printable report
 * and the medals (a meet of a medal phase) are the two side views, opened
 * from the header's toolbar and kept in `?tab=` so Back and a shared link
 * restore them.
 *
 * Earlier links keep working: `?tab=info` (and `details`, `officials`)
 * opens the meet (its form is always open), `?tab=results` is the meet,
 * `?tab=print` the report; `/meets/$meetId/start-list` lands on the meet.
 */
export const SEARCH_TABS = ["results", "info", "report", "medals"] as const;
export type SearchTab = (typeof SEARCH_TABS)[number];

/** Earlier tab names that now open another tab. */
const ALIASES: Readonly<Record<string, SearchTab>> = { details: "info", officials: "info", print: "report" };

/** `?tab=` → a search tab, or undefined. Pure. */
export function parseMeetTab(raw: unknown): SearchTab | undefined {
	if (typeof raw !== "string") return undefined;
	if ((SEARCH_TABS as readonly string[]).includes(raw)) return raw as SearchTab;
	return ALIASES[raw];
}

/** The validated search of `/meets/$meetId`. */
export function meetSearch(search: Record<string, unknown>): { tab?: SearchTab } {
	const tab = parseMeetTab(search.tab);
	return tab ? { tab } : {};
}

/** What the page shows: the meet itself (start list or results), or one of its side views. */
export type MeetView = "meet" | "report" | "medals";

export function meetViewOf(tab: SearchTab | undefined, meet: Pick<MeetDetail, "phase">): MeetView {
	if (tab === "report") return "report";
	// Medals only exist on a meet of a medal phase; an old link elsewhere opens the meet.
	if (tab === "medals" && meet.phase.awards_medals) return "medals";
	return "meet";
}

/** The old OVR's two modes: build the start list until the meet goes live, then score it. */
export type MeetMode = "start-list" | "results";

export function meetMode(status: ResultStatus): MeetMode {
	return status === "NONE" || status === "START_LIST" ? "start-list" : "results";
}
